import React, { useEffect, useState } from "react";
import { Eye, Heart, MessageCircle, BookOpen, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Lot 698 — compteurs du VENDEUR dans sa fiche : vues, favoris, messages et « depuis ta story » (membres qui ont ouvert
// l'annonce via le bouton de la story). Fenêtre = depuis le début de la promo en cours, sinon depuis la publication.
// Sous les compteurs : bilan de la dernière promo terminée (promo_history), le même que la notification reçue.
const fmtDay = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang || "fr", { day: "numeric", month: "short" }) : "");

const Stat = ({ icon: Icon, value, label, color, testId, title }) => (
  <div className="rounded-xl px-2 py-2 text-center" style={{ background: "var(--yalola-surface)" }} data-testid={testId} title={title}>
    <Icon size={14} className="mx-auto" style={{ color }} />
    <div className="text-base font-bold leading-tight mt-0.5 tabular-nums" style={{ fontFamily: "Outfit" }}>{value}</div>
    <div className="text-[10px] leading-tight" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
  </div>
);

export const ListingStats = ({ it }) => {
  const { t, lang } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    setS(null);
    if (!it?.id) return undefined;
    api.get(`/marketplace/listings/${it.id}/stats`).then(({ data }) => { if (alive) setS(data); }).catch(() => { if (alive) setS(null); });
    return () => { alive = false; };
    // Re-chargé quand la promo change (lancée / arrêtée / prolongée) : la fenêtre des compteurs n'est plus la même.
  }, [it?.id, it?.promo?.started_at, it?.promo?.ends_at]);
  if (!s) return null;
  const r = s.last_recap;
  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="listing-stats" data-scope={s.scope}>
      <div className="text-[11px] font-semibold uppercase tracking-wide inline-flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
        <BarChart3 size={12} /> {s.scope === "promo" ? t("market.stats_promo_title", "Depuis le début de ta promo") : t("market.stats_all_title", "Depuis la publication")}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <Stat icon={Eye} value={s.views} label={t("market.stats_views", "vues")} color="var(--yalola-primary)" testId="listing-stats-views" />
        <Stat icon={Heart} value={s.favorites} label={t("market.stats_favorites", "favoris")} color="#D97757" testId="listing-stats-favorites" />
        <Stat icon={MessageCircle} value={s.messages} label={t("market.stats_messages", "messages")} color="#2D9E68" testId="listing-stats-messages" />
        <Stat icon={BookOpen} value={s.story_opens} label={t("market.stats_story", "depuis ta story")} color="#D64545" testId="listing-stats-story"
          title={t("market.stats_story_hint", "{n} membre(s) ont ouvert ton annonce depuis ta story").replace("{n}", String(s.story_opens))} />
      </div>
      {s.scope === "promo" && s.story_opens === 0 && (
        <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="listing-stats-story-none">{t("market.stats_story_none", "Partage ta promo en story pour toucher plus de monde")}</p>
      )}
      {r && (
        <div className="pt-2 text-xs" style={{ borderTop: "1px solid var(--yalola-border)" }} data-testid="listing-promo-recap">
          <span className="font-semibold">{t("market.promo_recap_title", "Bilan de ta dernière promo")}</span>
          <span style={{ color: "var(--yalola-text-muted)" }}> · {t("market.promo_recap_period", "-{n} % · du {from} au {to}").replace("{n}", String(r.percent ?? 0)).replace("{from}", fmtDay(r.started_at, lang)).replace("{to}", fmtDay(r.ended_at, lang))}</span>
          <div className="mt-0.5 tabular-nums" data-testid="listing-promo-recap-stats">
            {r.views} {t("market.stats_views", "vues")} · {r.favorites} {t("market.stats_favorites", "favoris")} · {r.messages} {t("market.stats_messages", "messages")} · {r.story_opens} {t("market.stats_story", "depuis ta story")}
          </div>
        </div>
      )}
      {s.scope === "promo" && !r && <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("market.promo_recap_hint", "Tu recevras ce bilan en notification à la fin de chaque promo")}</p>}
    </div>
  );
};

export default ListingStats;
